import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { DateTime, } from 'luxon';
import PropTypes from "prop-types";
import { getTimePickerDefaultRange } from "./time-picker-defaults";

import "react-datepicker/dist/react-datepicker.css";
import 'react-datepicker/dist/react-datepicker-cssmodules.css';

const isValidDate = date => date instanceof Date && !isNaN(date.getTime());
const sameTime = (a, b) => (a ? a.getTime() : null) === (b ? b.getTime() : null);

// When the host passes startDate/endDate, the picker displays them and only reports user changes.
// Without them (e.g. Webtip) it falls back to its defaults and reports them once on mount, as before.
export const TimePicker = ({ mode, angularCallback, showResetButton = true, startDate: hostStartDate, endDate: hostEndDate }) => {

  const [hasHostDates] = useState(() => isValidDate(hostStartDate) && isValidDate(hostEndDate));
  const [initialRange] = useState(() => (hasHostDates
    ? { startDate: hostStartDate, endDate: hostEndDate }
    : getTimePickerDefaultRange()));
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [timeFormatString, setTimeFormatString] = useState("HH:mm");
  const [dateFormatStringDateOnly, setDateFormatStringDateOnly] = useState("dd/MM/yyyy");
  const [dateFormatStringHoursMinutes, setDateFormatStringHoursMinutes] = useState("dd/MM/yyyy HH:mm");
  
  const ResetButton = () => {
    if (!showResetButton) {
      return null;
    }
    
    return (
      <div>
        <button 
          className="btn btn-secondary"
          data-testid="time-picker-reset-dates-button"
          onClick={() => {
            const defaults = getTimePickerDefaultRange();
            setStartDate(defaults.startDate);
            setEndDate(defaults.endDate);
            callAngularCallback(defaults.startDate, defaults.endDate);
          }}>
            <i className="fas fa-undo fa-fw" aria-hidden="true" /> Reset dates
        </button>
      </div>
    );
  };

  const callAngularCallback = (start, end) => {
    if (angularCallback) {
      // Send the modified date(s) back to Angular
      if (mode === "dateAndTimeStartEnd" || mode === "dateStartEnd") {
        angularCallback(start, end);
      } else {
        angularCallback(start);
      }
    }
  }

  const handleStartDateChange = date => {
    setStartDate(date);
    callAngularCallback(date, endDate);
  };

  const handleEndDateChange = date => {
    setEndDate(date);
    callAngularCallback(startDate, date);
  };

  // Called once when the component initializes
  useEffect(() => {
    const currentTimezone = DateTime.local().zoneName;

    // Formattings based on the current time zone of the Browser.
    // Use defaults for Australia, Brazil and UK. If Finland, use the dots instead of dashes
    if (currentTimezone === "Europe/Helsinki") {
      setTimeFormatString("HH.mm");
      setDateFormatStringDateOnly("dd.MM.yyyy");
      setDateFormatStringHoursMinutes("dd.MM.yyyy HH.mm");
    }
    // Any unmounting code here
    return () => {
    };
  }, []);

  // Hosts without their own dates still need the defaults reported once on mount.
  useEffect(() => {
    if (!hasHostDates) {
      callAngularCallback(initialRange.startDate, initialRange.endDate);
    }
  }, []);

  // Reflect dates the host changes itself (e.g. heat map paging) without reporting them back.
  useEffect(() => {
    if (isValidDate(hostStartDate) && !sameTime(hostStartDate, startDate)) {
      setStartDate(hostStartDate);
    }
    if (isValidDate(hostEndDate) && !sameTime(hostEndDate, endDate)) {
      setEndDate(hostEndDate);
    }
  }, [hostStartDate, hostEndDate]);

  switch (mode) {
    case "dateAndTimeStartEnd":
    default:
      // Start and end dates with time selection. Used in Webtip
      return (
        <>
          <div className="time-picker time-picker--with-time" data-testid="time-picker-date-time-range">
            <div className="time-picker__field">
              <DatePicker
                popperPlacement="bottom-start"
                selectsStart
                customInput={<input className="form-control" data-testid="time-picker-start-date" />}
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                selected={startDate}
                onChange={handleStartDateChange}
                showTimeSelect
                timeFormat={timeFormatString}
                timeIntervals={15}
                dateFormat={dateFormatStringHoursMinutes}
                showIcon={true}
                toggleCalendarOnIconClick
              />
              </div>
            <div className="time-picker__field">
              <DatePicker
                popperPlacement="bottom-start"
                selectsEnd
                customInput={<input className="form-control" data-testid="time-picker-end-date" />}
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                selected={endDate}
                onChange={handleEndDateChange}
                showTimeSelect
                timeFormat={timeFormatString}
                timeIntervals={15}
                dateFormat={dateFormatStringHoursMinutes}
                showIcon={true} 
                toggleCalendarOnIconClick
              />
            </div>
            <ResetButton />
          </div>
        </>
      );
    case "dateStartEnd":
      // Start and end dates without time selection. Used in Analysis
      return (
        <>
          <div className="time-picker" data-testid="time-picker-date-range">
            <div className="time-picker__field">
              <DatePicker
                popperPlacement="bottom-start"
                selectsStart
                customInput={<input className="form-control" data-testid="time-picker-start-date" />}
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                selected={startDate}
                onChange={handleStartDateChange}
                dateFormat={dateFormatStringDateOnly}
                showIcon={true}
                toggleCalendarOnIconClick
              />
            </div>
            <div className="time-picker__field">
              <DatePicker
                popperPlacement="bottom-start"
                selectsEnd
                customInput={<input className="form-control" data-testid="time-picker-end-date" />}
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                selected={endDate}
                onChange={handleEndDateChange}
                dateFormat={dateFormatStringDateOnly}
                showIcon={true} 
                toggleCalendarOnIconClick
                />
            </div>
            <ResetButton />
          </div>
        </>
      );
  };
};
TimePicker.propTypes = {
  mode: PropTypes.string,
  timeStamp: PropTypes.object,
  angularCallback: PropTypes.func,
  showResetButton: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
};
